import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { createPlaylist, deletePlaylist, getPlaylists, getTracks, reportNowPlaying, updatePlaylist } from '../../api/client';
import { getMediaType } from '../../utils/media';
import { parseUsernameFromToken } from '../../utils/authToken';
import { RADIO_TRACK_SENTINEL_ID } from '../../utils/radioConfig';
import type { PlaylistApi, TrackApi } from '../../api/client';
import TopBar from './components/TopBar';
import PlayerControls, { type PlayerControlsHandle } from './components/PlayerControls';
import PlaylistSection from './components/PlaylistSection';
import SubscriberList from './components/SubscriberList';
import Recommendations from './components/Recommendations';
import PlaylistManager from './components/PlaylistManager';
import RadioStation from './components/RadioStation';
import TabBar from './components/TabBar';
import StoriesSection from './components/StoriesSection';
import PlaylistSwitcher from './components/PlaylistSwitcher';

function mapApiPlaylist(p: PlaylistApi): Playlist {
  const rawCover = p.cover_image ?? p.coverImage ?? '';
  const coverImage = typeof rawCover === 'string' && rawCover.trim().length > 0 ? rawCover.trim() : '';
  return {
    id: p.id,
    name: p.name,
    description: p.description || '',
    tracks: (p.tracks || []).map(mapApiTrack),
    coverImage,
    createdAt: p.created_at ?? p.createdAt ?? '',
    visibility: p.visibility ?? 'public',
    isLiked: !!p.is_liked,
    likesCount: p.likes_count ?? 0,
    commentsCount: p.comments_count ?? 0,
    ownerUsername: p.owner_username ?? null,
    ownerAvatarUrl: p.owner_avatar_url ?? null,
  };
}
function mapApiTrack(t: TrackApi): Track {
  return {
    id: t.id,
    title: t.title,
    artist: t.artist,
    album: t.album,
    duration: t.duration,
    image: t.image || '',
    audioUrl: t.audio_url || undefined,
    isLiked: !!t.is_liked,
  };
}

export interface Track {
  id: number;
  title: string;
  artist: string;
  album: string;
  duration: string;
  image: string;
  audioUrl?: string;
  isLiked?: boolean;
}

export interface Playlist {
  id: number;
  name: string;
  description: string;
  tracks: Track[];
  coverImage: string;
  createdAt: string;
  visibility?: 'public' | 'followers' | 'private';
  isLiked?: boolean;
  likesCount?: number;
  commentsCount?: number;
  /** Present when loaded from API — used to hide remove on others' playlists. */
  ownerUsername?: string | null;
  ownerAvatarUrl?: string | null;
}

const EMPTY_TRACK: Track = {
  id: 0,
  title: '',
  artist: '',
  album: '',
  duration: '',
  image: '',
};

/** Parse "M:SS" or "MM:SS" to seconds */
function parseDuration(s: string): number {
  if (!s) return 0;
  const parts = s.trim().split(':').map(Number);
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 1) return parts[0];
  return 0;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const EMPTY_PLAYLIST: Playlist = {
  id: 0,
  name: '',
  description: '',
  tracks: [],
  coverImage: '',
  createdAt: '',
};

const CONTINUOUS_PLAY_STORAGE_KEY = 'frequency-locus-continuous-play';

const TAB_CONTENT: Record<string, { eyebrow: string; title: string; description: string }> = {
  playlist: {
    eyebrow: 'Listen now',
    title: 'Music, radio, and stories in one place',
    description: 'Jump into live radio, queue up playlists, and keep the cultural pulse close.',
  },
  manage: {
    eyebrow: 'Your library',
    title: 'Build playlists that feel intentional',
    description: 'Create, organize, and collaborate on collections without digging through clutter.',
  },
  recommendations: {
    eyebrow: 'Discover',
    title: 'Find the next sound worth saving',
    description: 'Browse catalog picks and drop standout tracks straight into your playlists.',
  },
  subscribers: {
    eyebrow: 'Community',
    title: 'See who is listening with you',
    description: 'Follow people, discover what they are playing, and stay close to your circle.',
  },
};

export default function Home() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isLoggedIn, isPro, username, logout, token } = useAuth();
  /** Context username can lag behind the session token on first paint; reorder/remove need this for canEditTracks. */
  const effectiveUsername = useMemo(
    () => username ?? parseUsernameFromToken(token),
    [username, token]
  );
  const [activeTab, setActiveTab] = useState('playlist');
  const [currentTrack, setCurrentTrack] = useState<Track>(EMPTY_TRACK);
  const [isPlaying, setIsPlaying] = useState(false);
  /** YouTube/SoundCloud background iframe — no <audio> events, so we sync from PlayerControls for UI. */
  const [embedListenPlaying, setEmbedListenPlaying] = useState(false);
  /** When on, advance to the next playable track in the selected playlist after each direct audio file ends (embed URLs cannot report “ended” reliably). */
  const [continuousPlay, setContinuousPlay] = useState(() => {
    try {
      return typeof localStorage !== 'undefined' && localStorage.getItem(CONTINUOUS_PLAY_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [selectedPlaylist, setSelectedPlaylist] = useState<number>(0);
  const [isRadioPlaying, setIsRadioPlaying] = useState(false);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [availableTracks, setAvailableTracks] = useState<Track[]>([]);
  const [loading, setLoading] = useState(true);
  const [playlistLimitReached, setPlaylistLimitReached] = useState(false);
  const [playProgress, setPlayProgress] = useState(0);
  const [playCurrentTime, setPlayCurrentTime] = useState('0:00');
  const audioRef = useRef<HTMLAudioElement>(null);
  const playerControlsRef = useRef<PlayerControlsHandle | null>(null);

  /** YouTube / SoundCloud: load track then open the embed player (explicit video). */
  const handlePlayVideoEmbed = useCallback((track: Track) => {
    setCurrentTrack(track);
    requestAnimationFrame(() => playerControlsRef.current?.expandEmbed());
  }, []);

  // Sync audio source when currentTrack changes (skip for embed URLs — the iframe handles them).
  // For direct audio URLs, auto-start playback once the file can play (picking a track then “does nothing” was confusing).
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const url = currentTrack.audioUrl || '';
    const mediaType = getMediaType(url);
    if (mediaType === 'youtube' || mediaType === 'soundcloud') {
      el.src = '';
      el.load();
      setPlayProgress(0);
      setPlayCurrentTime('0:00');
      setIsPlaying(false);
      return;
    }
    el.src = url;
    el.load();
    setPlayProgress(0);
    setPlayCurrentTime('0:00');
    setIsPlaying(false);
    if (!url.trim() || mediaType === 'none') return;
    const onCanPlay = () => {
      el.removeEventListener('canplay', onCanPlay);
      void el.play().catch(() => setIsPlaying(false));
    };
    el.addEventListener('canplay', onCanPlay);
    return () => {
      el.removeEventListener('canplay', onCanPlay);
    };
  }, [currentTrack.id, currentTrack.audioUrl]);

  // Keep embedded radio streams (YouTube/SoundCloud) in sync with the radio card button.
  useEffect(() => {
    if (currentTrack.id !== RADIO_TRACK_SENTINEL_ID) return;
    const mediaType = getMediaType(currentTrack.audioUrl || '');
    if (mediaType !== 'youtube' && mediaType !== 'soundcloud') return;
    if (isRadioPlaying !== embedListenPlaying) {
      playerControlsRef.current?.toggleBackgroundEmbed();
    }
  }, [
    currentTrack.id,
    currentTrack.audioUrl,
    isRadioPlaying,
    embedListenPlaying,
  ]);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (!currentTrack.id || currentTrack.id === RADIO_TRACK_SENTINEL_ID) return;
    reportNowPlaying(currentTrack.id).catch(() => {});
  }, [currentTrack.id, isLoggedIn]);

  useEffect(() => {
    try {
      localStorage.setItem(CONTINUOUS_PLAY_STORAGE_KEY, continuousPlay ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [continuousPlay]);

  const getCurrentPlaylist = useCallback((): Playlist => {
    if (playlists.length === 0) return EMPTY_PLAYLIST;
    return playlists.find((p) => p.id === selectedPlaylist) || playlists[0];
  }, [playlists, selectedPlaylist]);

  const advanceToNextPlaylistTrack = useCallback(() => {
    const pl = getCurrentPlaylist();
    if (!pl.tracks.length) return;
    const idx = pl.tracks.findIndex((t) => t.id === currentTrack.id);
    if (idx === -1) return;
    let nextIdx = idx + 1;
    while (nextIdx < pl.tracks.length) {
      const t = pl.tracks[nextIdx];
      const url = t.audioUrl || '';
      if (url.trim() && getMediaType(url) !== 'none') {
        setCurrentTrack(t);
        const mt = getMediaType(t.audioUrl);
        if (mt === 'youtube' || mt === 'soundcloud') {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => playerControlsRef.current?.toggleBackgroundEmbed());
          });
        }
        return;
      }
      nextIdx += 1;
    }
  }, [currentTrack.id, getCurrentPlaylist]);

  // Sync isPlaying with audio events and update progress
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      setPlayProgress(0);
      setPlayCurrentTime('0:00');
      if (continuousPlay) {
        advanceToNextPlaylistTrack();
      }
    };
    const onTimeUpdate = () => {
      if (el.duration && isFinite(el.duration)) {
        const p = (el.currentTime / el.duration) * 100;
        setPlayProgress(p);
        setPlayCurrentTime(formatTime(el.currentTime));
      }
    };
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    el.addEventListener('timeupdate', onTimeUpdate);
    return () => {
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('timeupdate', onTimeUpdate);
    };
  }, [continuousPlay, advanceToNextPlaylistTrack]);

  const handlePlayPause = () => {
    const el = audioRef.current;
    if (!el) return;
    if (!currentTrack.audioUrl) return;
    const mediaType = getMediaType(currentTrack.audioUrl);
    if (mediaType === 'youtube' || mediaType === 'soundcloud') return; // iframe handles playback
    if (isPlaying) el.pause();
    else el.play().catch(() => setIsPlaying(false));
  };

  const handleSeek = (percent: number) => {
    const el = audioRef.current;
    if (!el || !currentTrack.audioUrl || !isFinite(el.duration)) return;
    el.currentTime = (percent / 100) * el.duration;
    setPlayProgress(percent);
    setPlayCurrentTime(formatTime(el.currentTime));
  };

  /** Track row: pause/resume direct audio, or toggle hidden embed audio (no video panel) for YouTube/SoundCloud. */
  const handlePlaybackToggleFromList = () => {
    const url = currentTrack.audioUrl || '';
    const mt = getMediaType(url);
    if (mt === 'youtube' || mt === 'soundcloud') {
      playerControlsRef.current?.toggleBackgroundEmbed();
      return;
    }
    handlePlayPause();
  };

  // Fetch playlists whenever auth state changes:
  // - on mount (guests see public playlists, logged-in users see all their playlists)
  // - on login: re-fetch to include private/followers playlists
  // - on logout: re-fetch so private playlists are replaced with only public ones instantly
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [pl, tr] = await Promise.all([getPlaylists(), getTracks()]);
        if (cancelled) return;
        const mapped = pl.map(mapApiPlaylist);
        setPlaylists(mapped);
        setAvailableTracks(tr.map(mapApiTrack));
        setSelectedPlaylist((prev) => (mapped.length > 0 && prev === 0 ? mapped[0].id : prev));
      } catch {
        if (!cancelled) {
          setPlaylists([]);
          setAvailableTracks([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isLoggedIn]);

  /** Deep link: /?playlist=ID&track=ID — open Listening room on a playlist (and optional track). */
  useEffect(() => {
    if (loading) return;
    const pid = searchParams.get('playlist');
    if (!pid) return;
    const id = parseInt(pid, 10);
    if (isNaN(id)) return;
    const pl = playlists.find((p) => p.id === id);
    if (!pl) return;
    setSelectedPlaylist(id);
    setActiveTab('playlist');
    const tid = searchParams.get('track');
    if (tid) {
      const trackId = parseInt(tid, 10);
      if (!isNaN(trackId)) {
        const t = pl.tracks.find((tr) => tr.id === trackId);
        if (t) setCurrentTrack(t);
      }
    }
  }, [loading, playlists, searchParams]);

  useEffect(() => {
    if (isPro || playlists.length < 3) {
      setPlaylistLimitReached(false);
    }
  }, [isPro, playlists.length]);

  const addPlaylist = async (name: string, description: string, visibility: 'public' | 'followers' | 'private' = 'public') => {
    if (!isLoggedIn) return;
    try {
      const created = await createPlaylist({ name, description, visibility });
      const mapped = mapApiPlaylist(created);
      setPlaylists((prev) => [mapped, ...prev]);
      setSelectedPlaylist(mapped.id);
      setPlaylistLimitReached(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('PLAYLIST_LIMIT') || msg.toLowerCase().includes('limit reached')) {
        setPlaylistLimitReached(true);
      }
      console.error(err);
    }
  };

  const deletePlaylistById = async (playlistId: number) => {
    try {
      await deletePlaylist(playlistId);
      setPlaylists((prev) => {
        const next = prev.filter((p) => p.id !== playlistId);
        if (selectedPlaylist === playlistId && next.length > 0) setSelectedPlaylist(next[0].id);
        return next;
      });
    } catch (err) {
      console.error(err);
    }
  };

  const addTrackToPlaylist = async (playlistId: number, track: Track) => {
    const target = playlists.find((p) => p.id === playlistId);
    if (!target) return;
    if (target.tracks.some((t) => t.id === track.id)) return;
    const trackIds = [...target.tracks.map((t) => t.id), track.id];
    try {
      const updated = await updatePlaylist(playlistId, { trackIds });
      setPlaylists((prev) => prev.map((p) => (p.id === playlistId ? mapApiPlaylist(updated) : p)));
    } catch (err) {
      console.error(err);
    }
  };

  const removeTrackFromPlaylist = async (playlistId: number, trackId: number) => {
    const target = playlists.find((p) => p.id === playlistId);
    if (!target) return;
    const trackIds = target.tracks.filter((t) => t.id !== trackId).map((t) => t.id);
    try {
      const updated = await updatePlaylist(playlistId, { trackIds });
      setPlaylists((prev) => prev.map((p) => (p.id === playlistId ? mapApiPlaylist(updated) : p)));
    } catch (err) {
      console.error(err);
    }
  };

  const reorderPlaylistTracks = async (playlistId: number, trackIds: number[]) => {
    try {
      const updated = await updatePlaylist(playlistId, { trackIds });
      setPlaylists((prev) => prev.map((p) => (p.id === playlistId ? mapApiPlaylist(updated) : p)));
    } catch (err) {
      console.error(err);
    }
  };

  const renderContent = () => {
    const pl = getCurrentPlaylist();
    const canEditTracks =
      !pl.ownerUsername ||
      (effectiveUsername != null &&
        pl.ownerUsername.toLowerCase() === effectiveUsername.toLowerCase());
    switch (activeTab) {
      case 'playlist':
        return (
          <div>
            <PlaylistSwitcher
              playlists={playlists}
              selectedPlaylistId={getCurrentPlaylist().id}
              onSelectPlaylist={setSelectedPlaylist}
              onGoToManage={() => setActiveTab('manage')}
            />
            <div className="lg:grid lg:grid-cols-12 lg:gap-8">
              <section className="lg:col-span-5 xl:col-span-4">
                <RadioStation
                  isRadioPlaying={isRadioPlaying}
                  setIsRadioPlaying={setIsRadioPlaying}
                  setCurrentTrack={setCurrentTrack}
                  setIsPlaying={setIsPlaying}
                />
              </section>
              <section className="lg:col-span-7 xl:col-span-8 mt-6 lg:mt-0">
                <PlaylistSection
                  currentTrack={currentTrack}
                  setCurrentTrack={setCurrentTrack}
                  playlist={pl}
                  onRemoveTrack={removeTrackFromPlaylist}
                  canRemoveTracks={canEditTracks}
                  isPlaying={isPlaying || embedListenPlaying}
                  onTogglePlayback={handlePlaybackToggleFromList}
                  onPlayVideoEmbed={handlePlayVideoEmbed}
                  continuousPlay={continuousPlay}
                  onContinuousPlayChange={setContinuousPlay}
                  onReorderTracks={canEditTracks ? reorderPlaylistTracks : undefined}
                />
              </section>
            </div>
          </div>
        );
      case 'manage':
        return (
          <div className="space-y-4">
            {playlistLimitReached && !isPro && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-900">
                You&apos;ve reached the 3-playlist limit on the free plan.{' '}
                <a href="/pricing" className="font-medium underline underline-offset-2">
                  Upgrade to Pro
                </a>
                .
              </div>
            )}
            <PlaylistManager 
              playlists={playlists}
              selectedPlaylist={selectedPlaylist}
              setSelectedPlaylist={setSelectedPlaylist}
              onAddPlaylist={addPlaylist}
              onDeletePlaylist={deletePlaylistById}
              onAddTrack={addTrackToPlaylist}
              availableTracks={availableTracks}
            />
          </div>
        );
      case 'subscribers':
        return <SubscriberList />;
      case 'recommendations':
        return <Recommendations setCurrentTrack={setCurrentTrack} onAddToPlaylist={addTrackToPlaylist} playlists={playlists} availableTracks={availableTracks} />;
      default:
        return (
          <div>
            <PlaylistSwitcher
              playlists={playlists}
              selectedPlaylistId={getCurrentPlaylist().id}
              onSelectPlaylist={setSelectedPlaylist}
              onGoToManage={() => setActiveTab('manage')}
            />
            <div className="lg:grid lg:grid-cols-12 lg:gap-8">
              <section className="lg:col-span-5 xl:col-span-4">
                <RadioStation
                  isRadioPlaying={isRadioPlaying}
                  setIsRadioPlaying={setIsRadioPlaying}
                  setCurrentTrack={setCurrentTrack}
                  setIsPlaying={setIsPlaying}
                />
              </section>
              <section className="lg:col-span-7 xl:col-span-8 mt-6 lg:mt-0">
                <PlaylistSection
                  currentTrack={currentTrack}
                  setCurrentTrack={setCurrentTrack}
                  playlist={pl}
                  onRemoveTrack={removeTrackFromPlaylist}
                  canRemoveTracks={canEditTracks}
                  isPlaying={isPlaying || embedListenPlaying}
                  onTogglePlayback={handlePlaybackToggleFromList}
                  onPlayVideoEmbed={handlePlayVideoEmbed}
                  continuousPlay={continuousPlay}
                  onContinuousPlayChange={setContinuousPlay}
                  onReorderTracks={canEditTracks ? reorderPlaylistTracks : undefined}
                />
              </section>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#fcfbf7_0%,#f6efe2_45%,#f5f7f2_100%)] text-stone-900 relative">
      <TopBar activeTab={activeTab} setActiveTab={setActiveTab} />

      <main className="pt-24 md:pt-28 pb-32 md:pb-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <section className="relative overflow-hidden rounded-[2rem] border border-stone-200/70 bg-[radial-gradient(circle_at_top_left,_rgba(251,191,36,0.18),_transparent_30%),linear-gradient(135deg,_#1c1917_0%,_#3f2a15_45%,_#6b3412_100%)] text-white shadow-[0_24px_80px_rgba(120,53,15,0.18)]">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute -top-12 right-8 h-40 w-40 rounded-full bg-amber-200 blur-3xl" />
              <div className="absolute bottom-0 left-0 h-48 w-48 rounded-full bg-orange-300 blur-3xl" />
            </div>
            <div className="relative grid gap-8 px-6 py-8 md:px-8 md:py-10 lg:grid-cols-[1.2fr_0.8fr]">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-200/90">
                  {TAB_CONTENT[activeTab]?.eyebrow ?? TAB_CONTENT.playlist.eyebrow}
                </p>
                <h1 className="mt-3 max-w-2xl text-3xl font-bold leading-tight md:text-5xl" style={{ fontFamily: 'Ubuntu, sans-serif' }}>
                  {TAB_CONTENT[activeTab]?.title ?? TAB_CONTENT.playlist.title}
                </h1>
                <p className="mt-4 max-w-2xl text-sm leading-6 text-amber-50/85 md:text-base">
                  {TAB_CONTENT[activeTab]?.description ?? TAB_CONTENT.playlist.description}
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <div className="rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm text-amber-50/90">
                    {playlists.length} playlist{playlists.length === 1 ? '' : 's'}
                  </div>
                  <div className="rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm text-amber-50/90">
                    {availableTracks.length} track{availableTracks.length === 1 ? '' : 's'} in catalog
                  </div>
                  <div className="rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm text-amber-50/90">
                    {isLoggedIn ? `Signed in as ${username}` : 'Guest mode'}
                  </div>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
                <div className="rounded-3xl border border-white/10 bg-white/10 p-5 backdrop-blur-sm">
                  <p className="text-xs uppercase tracking-[0.2em] text-amber-200/80">Current playlist</p>
                  {playlists.length > 0 ? (
                    <>
                      <label htmlFor="hero-playlist-select" className="sr-only">
                        Choose playlist
                      </label>
                      <select
                        id="hero-playlist-select"
                        value={getCurrentPlaylist().id}
                        onChange={(e) => setSelectedPlaylist(Number(e.target.value))}
                        className="mt-3 w-full cursor-pointer rounded-xl border border-white/25 bg-stone-950/40 px-3 py-2.5 text-lg font-semibold text-white focus:border-amber-300/60 focus:outline-none focus:ring-2 focus:ring-amber-200/30"
                      >
                        {playlists.map((p) => (
                          <option key={p.id} value={p.id} className="bg-stone-900 text-white">
                            {p.name} ({p.tracks.length} tracks)
                          </option>
                        ))}
                      </select>
                      <p className="mt-2 text-sm text-amber-50/75">
                        {getCurrentPlaylist().description || 'Tracks for this list appear in the listening room below.'}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="mt-3 text-xl font-semibold">Nothing selected yet</p>
                      <p className="mt-1 text-sm text-amber-50/75">
                        Create a playlist in Manage, then pick it here or in the listening room.
                      </p>
                    </>
                  )}
                </div>
                <div className="rounded-3xl border border-white/10 bg-white/10 p-5 backdrop-blur-sm">
                  <p className="text-xs uppercase tracking-[0.2em] text-amber-200/80">Now playing</p>
                  <p className="mt-3 text-xl font-semibold">{currentTrack.title || 'No track selected'}</p>
                  <p className="mt-1 text-sm text-amber-50/75">
                    {currentTrack.artist || 'Pick a track from your playlists or use live radio to start.'}
                  </p>
                </div>
                <div className="rounded-3xl border border-white/10 bg-white/10 p-5 backdrop-blur-sm">
                  <p className="text-xs uppercase tracking-[0.2em] text-amber-200/80">Community pulse</p>
                  <p className="mt-3 text-xl font-semibold">
                    {isLoggedIn ? 'Your feed is ready' : 'Sign in to connect'}
                  </p>
                  <p className="mt-1 text-sm text-amber-50/75">
                    Follow people, react to playlists, and keep up with what your circle is listening to.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {activeTab === 'playlist' && (
            <section className="mt-8 md:mt-10 mb-6 md:mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-stone-900 mb-2" style={{ fontFamily: 'Ubuntu, sans-serif' }}>
                Listening room
              </h2>
              <p className="text-sm md:text-base text-stone-600">
                Pick a playlist (hero or list below), then tap any track to play. Use{' '}
                <span className="font-medium text-stone-800">Manage</span> to create or reorder playlists.
              </p>
            </section>
          )}

          <div className={activeTab !== 'playlist' ? 'mt-8 md:mt-10' : ''}>
            {loading ? (
              <div className="rounded-3xl border border-stone-200/70 bg-white/75 px-6 py-12 text-center text-stone-500 shadow-sm">
                Loading playlists...
              </div>
            ) : (
              renderContent()
            )}
          </div>
          {!isLoggedIn && (
            <p className="mt-8 text-center text-sm text-stone-500">
              Log in to create and share playlists.
            </p>
          )}

          {activeTab === 'playlist' && <StoriesSection />}

          {activeTab === 'playlist' && (
            <section className="mt-14 md:mt-16">
              <div className="rounded-[2rem] border border-stone-200/70 bg-white/80 px-6 py-8 shadow-sm">
                <div className="flex flex-col gap-1 mb-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.26em] text-amber-600">Plans</p>
                  <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Ubuntu, sans-serif' }}>
                    {isPro ? 'You\'re on Pro — enjoy every feature.' : 'Free vs Pro'}
                  </h2>
                  {!isPro && (
                    <p className="text-sm text-stone-600">Upgrade to unlock unlimited playlists, private visibility, and collaborators.</p>
                  )}
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  {/* Free column */}
                  <div className="rounded-2xl border border-stone-200 bg-stone-50/80 p-5">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-semibold text-stone-700 uppercase tracking-wide">Free</span>
                      {!isPro && (
                        <span className="rounded-full bg-stone-200 px-3 py-0.5 text-xs font-medium text-stone-600">Current plan</span>
                      )}
                    </div>
                    <ul className="space-y-3 text-sm text-stone-700">
                      {[
                        'Stream the full music catalog',
                        'Up to 3 playlists',
                        'Public playlists only',
                        'Follow people & react to playlists',
                        'Stories, comments & radio',
                      ].map((f) => (
                        <li key={f} className="flex items-start gap-2.5">
                          <i className="ri-check-line text-emerald-500 mt-0.5 shrink-0"></i>
                          <span>{f}</span>
                        </li>
                      ))}
                      {[
                        'Unlimited playlists',
                        'Private & followers-only playlists',
                        'Playlist collaborators',
                      ].map((f) => (
                        <li key={f} className="flex items-start gap-2.5 opacity-40">
                          <i className="ri-lock-line text-stone-400 mt-0.5 shrink-0"></i>
                          <span className="line-through decoration-stone-400">{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Pro column */}
                  <div className={`rounded-2xl border p-5 ${isPro ? 'border-amber-300 bg-[radial-gradient(circle_at_top_left,rgba(251,191,36,0.12),transparent_60%),linear-gradient(135deg,#1c1917_0%,#3f2a15_100%)] text-white' : 'border-amber-200 bg-amber-50/60'}`}>
                    <div className="flex items-center justify-between mb-4">
                      <span className={`text-sm font-semibold uppercase tracking-wide ${isPro ? 'text-amber-300' : 'text-amber-700'}`}>Pro</span>
                      <span className={`rounded-full px-3 py-0.5 text-xs font-medium ${isPro ? 'bg-amber-400/20 text-amber-200' : 'bg-amber-100 text-amber-700'}`}>
                        {isPro ? 'Your plan' : '$5.99 / month'}
                      </span>
                    </div>
                    <ul className="space-y-3 text-sm">
                      {[
                        'Everything in Free',
                        'Unlimited playlists',
                        'Private & followers-only playlists',
                        'Invite collaborators to playlists',
                        'Early access to new features',
                      ].map((f) => (
                        <li key={f} className={`flex items-start gap-2.5 ${isPro ? 'text-amber-50/90' : 'text-amber-900'}`}>
                          <i className={`ri-star-line mt-0.5 shrink-0 ${isPro ? 'text-amber-400' : 'text-amber-500'}`}></i>
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    {!isPro && isLoggedIn && (
                      <a
                        href="/pricing"
                        className="mt-5 flex w-full items-center justify-center gap-1.5 rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-amber-600 transition-colors"
                      >
                        Upgrade to Pro <i className="ri-arrow-right-line"></i>
                      </a>
                    )}
                    {!isLoggedIn && (
                      <a
                        href="/register"
                        className="mt-5 flex w-full items-center justify-center gap-1.5 rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-amber-600 transition-colors"
                      >
                        Create a free account <i className="ri-arrow-right-line"></i>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </section>
          )}

          <footer className="mt-14 md:mt-16 rounded-[2rem] border border-stone-200/70 bg-white/80 px-6 py-6 shadow-sm">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-base font-semibold text-stone-900">Frequency Locus</p>
                <p className="text-sm text-stone-600">A calmer home for music, radio, stories, and community.</p>
              </div>
              <div className="flex flex-wrap items-center gap-5 text-sm text-stone-600">
                <button type="button" onClick={() => navigate('/profile')} className="hover:text-amber-700">Profile</button>
                {isLoggedIn ? (
                  <>
                    <span className="text-amber-700">{username}</span>
                    <button type="button" onClick={() => logout()} className="hover:text-amber-700">Log out</button>
                  </>
                ) : (
                  <>
                    <button type="button" onClick={() => navigate('/login')} className="hover:text-amber-700">Log in</button>
                    <button type="button" onClick={() => navigate('/register')} className="hover:text-amber-700">Create account</button>
                  </>
                )}
              </div>
            </div>
            <div className="mt-5 border-t border-stone-200 pt-4 text-xs text-stone-500">
              © {new Date().getFullYear()} Frequency Locus · Vibes from the motherland
            </div>
          </footer>
        </div>
      </main>

      <audio ref={audioRef} preload="metadata" />
      <PlayerControls
        ref={playerControlsRef}
        currentTrack={currentTrack}
        audioUrl={currentTrack.audioUrl}
        isPlaying={isPlaying || embedListenPlaying}
        onPlayPause={handlePlayPause}
        canPlay={Boolean(currentTrack.audioUrl)}
        playProgress={playProgress}
        playCurrentTime={playCurrentTime}
        onSeek={handleSeek}
        onCloseVideo={() => setCurrentTrack(EMPTY_TRACK)}
        embedListenPlaying={embedListenPlaying}
        setEmbedListenPlaying={setEmbedListenPlaying}
      />

      <TabBar activeTab={activeTab} setActiveTab={setActiveTab} />
    </div>
  );
}
